import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface WordFrequency {
  word: string;
  count: number;
  frequency: number; // Percentual
}

// Palavras comuns em português para filtrar
const STOP_WORDS = new Set([
  'a', 'o', 'e', 'de', 'do', 'da', 'em', 'um', 'uma', 'para', 'com', 'por', 'que',
  'é', 'no', 'na', 'os', 'as', 'ao', 'às', 'pelo', 'pela', 'dos', 'das', 'se',
  'não', 'mais', 'como', 'mas', 'foi', 'são', 'tem', 'ser', 'foi', 'está', 'estão',
  'foi', 'ter', 'ter', 'muito', 'já', 'também', 'quando', 'onde', 'quem', 'qual',
  'meu', 'minha', 'meus', 'minhas', 'teu', 'tua', 'teus', 'tuas', 'nosso', 'nossa',
  'você', 'vocês', 'ele', 'ela', 'eles', 'elas', 'nós', 'eu', 'tu', 'ele', 'ela',
  'isso', 'isto', 'aquilo', 'este', 'esta', 'esse', 'essa', 'aquele', 'aquela',
  'sobre', 'entre', 'até', 'após', 'sem', 'sob', 'perante', 'mediante', 'durante',
  'conforme', 'segundo', 'consoante', 'através', 'quanto', 'quanta', 'quantos', 'quantas',
  'qualquer', 'quaisquer', 'cada', 'todo', 'toda', 'todos', 'todas', 'outro', 'outra',
  'outros', 'outras', 'algum', 'alguma', 'alguns', 'algumas', 'nenhum', 'nenhuma',
  'nenhuns', 'nenhumas', 'todo', 'toda', 'todos', 'todas', 'ambos', 'ambas',
  'pouco', 'pouca', 'poucos', 'poucas', 'muito', 'muita', 'muitos', 'muitas',
  'tanto', 'tanta', 'tantos', 'tantas', 'quanto', 'quanta', 'quantos', 'quantas',
  'todo', 'toda', 'todos', 'todas', 'certo', 'certa', 'certos', 'certas',
  'vário', 'vária', 'vários', 'várias', 'outro', 'outra', 'outros', 'outras',
]);

export const useCSATWordFrequency = (daysRange: number = 30, limit: number = 20) => {
  return useQuery({
    queryKey: ['csat-word-frequency', daysRange, limit],
    queryFn: async (): Promise<WordFrequency[]> => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysRange);

      const { data, error } = await supabase
        .from('csat_responses')
        .select('comment')
        .gte('created_at', startDate.toISOString())
        .not('comment', 'is', null);

      if (error) throw error;

      if (!data || data.length === 0) {
        return [];
      }

      // Extrair palavras de todos os comentários
      const wordCounts: Record<string, number> = {};

      data.forEach(response => {
        if (!response.comment) return;

        // Normalizar texto: minúsculas, remover pontuação, dividir em palavras
        const text = response.comment
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Remove acentos
          .replace(/[^\w\s]/g, ' ') // Remove pontuação
          .split(/\s+/)
          .filter(word => word.length > 2); // Apenas palavras com mais de 2 caracteres

        text.forEach(word => {
          const normalizedWord = word.trim();
          if (normalizedWord && !STOP_WORDS.has(normalizedWord)) {
            wordCounts[normalizedWord] = (wordCounts[normalizedWord] || 0) + 1;
          }
        });
      });

      // Calcular frequência total
      const totalWords = Object.values(wordCounts).reduce((sum, count) => sum + count, 0);

      // Converter para array e ordenar
      const wordFrequencies: WordFrequency[] = Object.entries(wordCounts)
        .map(([word, count]) => ({
          word,
          count,
          frequency: totalWords > 0 ? Math.round((count / totalWords) * 100 * 10) / 10 : 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);

      return wordFrequencies;
    },
  });
};

